import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import AppLayout from "../components/AppLayout";
import {
  ReceiptText,
  Plus,
  Printer,
  Search,
  X,
  CalendarRange,
  MessageCircle,
  Mail,
  Loader2,
  Pencil,
  Send,
  Ban,
} from "lucide-react";
import { Button } from "../components/ui/button";
import SelectField from "../components/ui/SelectField";

const PAGE_SIZE = 10;
const API_URL = process.env.REACT_APP_API_URL;

const formatDateInput = (date) => date.toISOString().slice(0, 10);
const toNumber = (value) => {
  const p = Number(value);
  return Number.isNaN(p) ? 0 : p;
};
const clampPct = (value) => Math.max(0, Math.min(100, toNumber(value)));
const editItemFinal = (item) =>
  Math.max(0, toNumber(item.precio_original)) *
  (1 - clampPct(item.descuento_pct) / 100);
const lineKey = (item) =>
  [
    item.paciente_cod,
    item.fecha,
    item.sede_codigo,
    item.examen_cod,
    item.reg_exa,
  ].join("|");

const ModalWrapper = ({ onClose, children, wide }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div
      className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-5xl" : "max-w-lg"} max-h-[90vh] overflow-auto`}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const InvoicesPage = () => {
  const token = localStorage.getItem("token");
  const today = useMemo(() => formatDateInput(new Date()), []);

  const [filters, setFilters] = useState({ startDate: today, endDate: today });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showWAModal, setShowWAModal] = useState(false);
  const [waRow, setWaRow] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [waPhoneOriginal, setWaPhoneOriginal] = useState("");
  const [waEditing, setWaEditing] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRow, setEmailRow] = useState(null);
  const [emailAddress, setEmailAddress] = useState("");
  const [emailAddressOriginal, setEmailAddressOriginal] = useState("");
  const [emailEditing, setEmailEditing] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editFactura, setEditFactura] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editMeta, setEditMeta] = useState({ cliente: "N/A", propietario: "N/A" });
  const [editLoadingItems, setEditLoadingItems] = useState(false);
  const [editSaving, setEditSaving] = useState(false);

  const [showAnularModal, setShowAnularModal] = useState(false);
  const [anularRow, setAnularRow] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const [clientes, setClientes] = useState([]);
  const [propietarios, setPropietarios] = useState([]);
  const [createForm, setCreateForm] = useState({
    targetType: "cliente",
    targetCode: "",
    startDate: today,
    endDate: today,
    domicilio: "0",
    fechaFactura: today,
  });
  const [previewItems, setPreviewItems] = useState([]);
  const [previewMeta, setPreviewMeta] = useState({
    cliente: "N/A",
    propietario: "N/A",
  });
  const [globalDiscountPct, setGlobalDiscountPct] = useState(0);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: filters.startDate,
          endDate: filters.endDate,
          search,
          page,
          pageSize: PAGE_SIZE,
        },
      });
      setRows(res.data?.items || []);
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error(error);
      toast.error("No se pudieron cargar las facturas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [page, search, filters.startDate, filters.endDate]); // eslint-disable-line

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [cliRes, propRes] = await Promise.all([
          axios.get(`${API_URL}/util/clientes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/propietarios`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setClientes(Array.isArray(cliRes.data) ? cliRes.data : []);
        setPropietarios(Array.isArray(propRes.data) ? propRes.data : []);
      } catch (error) {
        console.error(error);
      }
    };
    loadOptions();
  }, [token]); // eslint-disable-line

  const resetCreateState = () => {
    setCreateForm({
      targetType: "cliente",
      targetCode: "",
      startDate: today,
      endDate: today,
      domicilio: "0",
      fechaFactura: today,
    });
    setPreviewItems([]);
    setPreviewMeta({ cliente: "N/A", propietario: "N/A" });
    setGlobalDiscountPct(0);
  };

  const openPreview = async () => {
    if (!createForm.targetCode) {
      toast.warning("Debe seleccionar cliente o propietario");
      return;
    }
    setPreviewing(true);
    try {
      const itemAdjustments = previewItems.map((item) => ({
        paciente_cod: item.paciente_cod,
        fecha: item.fecha,
        sede_codigo: item.sede_codigo,
        examen_cod: item.examen_cod,
        reg_exa: item.reg_exa,
        precio: toNumber(item.precio_base),
        descuentoPct: clampPct(item.descuento_item_pct),
      }));
      const res = await axios.post(
        `${API_URL}/invoices/preview`,
        {
          ...createForm,
          domicilio: Number(createForm.domicilio) || 0,
          globalDiscountPct,
          itemAdjustments,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreviewItems(res.data?.items || []);
      setPreviewMeta({
        cliente: res.data?.cliente || "N/A",
        propietario: res.data?.propietario || "N/A",
      });
      setGlobalDiscountPct(
        clampPct(res.data?.globalDiscountPct || globalDiscountPct),
      );
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "No se pudo previsualizar la factura",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const editSubtotal = useMemo(
    () => editItems.reduce((acc, item) => acc + editItemFinal(item), 0),
    [editItems],
  );

  const previewComputed = useMemo(() => {
    const subtotal = previewItems.reduce((acc, item) => {
      const base = Math.max(0, toNumber(item.precio_base));
      const itemPct = clampPct(item.descuento_item_pct);
      return (
        acc +
        base * (1 - itemPct / 100) * (1 - clampPct(globalDiscountPct) / 100)
      );
    }, 0);
    const domicilio = Math.max(0, Number(createForm.domicilio) || 0);
    return { subtotal, domicilio, total: subtotal + domicilio };
  }, [previewItems, globalDiscountPct, createForm.domicilio]);

  const updatePreviewItem = (key, field, value) => {
    setPreviewItems((prev) =>
      prev.map((item) => {
        if (lineKey(item) !== key) return item;
        if (field === "precio_base")
          return { ...item, precio_base: Math.max(0, toNumber(value)) };
        return { ...item, descuento_item_pct: clampPct(value) };
      }),
    );
  };

  const createInvoice = async () => {
    if (!previewItems.length) {
      toast.warning("Debe previsualizar la factura antes de generarla");
      return;
    }
    setCreating(true);
    try {
      const itemAdjustments = previewItems.map((item) => ({
        paciente_cod: item.paciente_cod,
        fecha: item.fecha,
        sede_codigo: item.sede_codigo,
        examen_cod: item.examen_cod,
        reg_exa: item.reg_exa,
        precio: toNumber(item.precio_base),
        descuentoPct: clampPct(item.descuento_item_pct),
      }));
      const res = await axios.post(
        `${API_URL}/invoices`,
        {
          ...createForm,
          domicilio: Number(createForm.domicilio) || 0,
          globalDiscountPct,
          itemAdjustments,
          fechaFactura: createForm.fechaFactura || today,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Factura ${res.data.factura} generada`);
      window.open(
        `${API_URL}/invoices/${res.data.factura}/pdf?domicilio=${Number(createForm.domicilio) || 0}`,
        "_blank",
        "noopener,noreferrer",
      );
      setShowPreviewModal(false);
      setShowModal(false);
      resetCreateState();
      setPage(1);
      fetchInvoices();
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "No fue posible generar la factura",
      );
    } finally {
      setCreating(false);
    }
  };

  const printInvoice = (factura) =>
    window.open(
      `${API_URL}/invoices/${factura}/pdf`,
      "_blank",
      "noopener,noreferrer",
    );

  const openEditModal = async (row) => {
    setEditFactura(row.factura);
    setEditItems([]);
    setEditMeta({ cliente: "N/A", propietario: "N/A" });
    setEditLoadingItems(true);
    setShowEditModal(true);
    try {
      const { data } = await axios.get(`${API_URL}/invoices/${row.factura}/items`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = (data.items || []).map((item) => ({
        ...item,
        precio_original:
          item.precio_original !== null && item.precio_original !== undefined
            ? item.precio_original
            : item.precio,
        descuento_pct: item.descuento_pct || 0,
      }));
      setEditItems(items);
      setEditMeta({
        cliente: data.cliente || "N/A",
        propietario: data.propietario || "N/A",
      });
    } catch {
      toast.error("No se pudieron cargar los items de la factura");
      setShowEditModal(false);
    } finally {
      setEditLoadingItems(false);
    }
  };

  const updateEditItem = (key, field, value) => {
    setEditItems((prev) =>
      prev.map((item) => {
        const k = [item.paciente_cod, item.fecha, item.sede_codigo, item.examen_cod, item.reg_exa].join("|");
        if (k !== key) return item;
        if (field === "precio_original")
          return { ...item, precio_original: Math.max(0, toNumber(value)) };
        return { ...item, descuento_pct: clampPct(value) };
      }),
    );
  };

  const saveEditInvoice = async () => {
    setEditSaving(true);
    try {
      await axios.put(
        `${API_URL}/invoices/${editFactura}`,
        {
          items: editItems.map((item) => ({
            paciente_cod: item.paciente_cod,
            fecha: item.fecha,
            sede_codigo: item.sede_codigo,
            examen_cod: item.examen_cod,
            reg_exa: item.reg_exa,
            precioOriginal: toNumber(item.precio_original),
            descuentoPct: clampPct(item.descuento_pct),
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Factura actualizada correctamente");
      setShowEditModal(false);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo actualizar la factura");
    } finally {
      setEditSaving(false);
    }
  };

  const openAnularModal = (row) => {
    setAnularRow(row);
    setShowAnularModal(true);
  };

  const confirmarAnular = async () => {
    if (!anularRow) return;
    setAnulando(true);
    try {
      await axios.patch(
        `${API_URL}/invoices/${anularRow.factura}/anular`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Factura ${anularRow.factura} anulada`);
      setShowAnularModal(false);
      setAnularRow(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.error || "No se pudo anular la factura");
    } finally {
      setAnulando(false);
    }
  };

  const openWAModal = async (row) => {
    setWaRow(row);
    setWaEditing(false);
    let phone = "";
    try {
      if (row.clte_codigo) {
        const { data } = await axios.get(
          `${API_URL}/whatsapp/telefono-cliente/${row.clte_codigo}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        phone = data.telefono || "";
      } else if (row.nit) {
        const { data } = await axios.get(
          `${API_URL}/whatsapp/telefono-propietario/${row.nit}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        phone = data.telefono || "";
      }
    } catch (_) {}
    setWaPhone(phone);
    setWaPhoneOriginal(phone);
    setShowWAModal(true);
  };

  const handleSendWAFactura = async () => {
    if (!waPhone.trim()) {
      toast.warning("Ingresa un número de teléfono");
      return;
    }
    setWaSending(true);
    try {
      if (waEditing && waPhone !== waPhoneOriginal && waRow) {
        if (waRow.clte_codigo) {
          await axios.patch(
            `${API_URL}/whatsapp/cliente/${waRow.clte_codigo}/telefono`,
            { telefono: waPhone },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else if (waRow.nit) {
          await axios.patch(
            `${API_URL}/whatsapp/propietario/${waRow.nit}/telefono`,
            { telefono: waPhone },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
        setWaPhoneOriginal(waPhone);
        setWaEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/whatsapp/enviar-factura`,
        {
          factura_id: waRow.factura,
          domicilio: 0,
          telefono: waPhone,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Factura enviada por WhatsApp correctamente");
      setShowWAModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por WhatsApp");
    } finally {
      setWaSending(false);
    }
  };

  const openEmailModal = async (row) => {
    setEmailRow(row);
    setEmailEditing(false);
    let email = "";
    try {
      if (row.clte_codigo) {
        const { data } = await axios.get(
          `${API_URL}/email/correo-cliente/${row.clte_codigo}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        email = data.email || "";
      } else if (row.nit) {
        const { data } = await axios.get(
          `${API_URL}/email/correo-propietario/${row.nit}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        email = data.email || "";
      }
    } catch (_) {}
    setEmailAddress(email);
    setEmailAddressOriginal(email);
    setShowEmailModal(true);
  };

  const handleSendEmailFactura = async () => {
    if (!emailAddress.trim()) {
      toast.warning("Ingresa un correo electrónico");
      return;
    }
    setEmailSending(true);
    try {
      if (emailEditing && emailAddress !== emailAddressOriginal && emailRow) {
        if (emailRow.clte_codigo) {
          await axios.patch(
            `${API_URL}/email/cliente/${emailRow.clte_codigo}/correo`,
            { email: emailAddress },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        } else if (emailRow.nit) {
          await axios.patch(
            `${API_URL}/email/propietario/${emailRow.nit}/correo`,
            { email: emailAddress },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }
        setEmailAddressOriginal(emailAddress);
        setEmailEditing(false);
      }
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(
        `${API_URL}/email/enviar-factura`,
        {
          factura_id: emailRow.factura,
          domicilio: 0,
          correo: emailAddress,
          enviado_por: user?.usuario || user?.id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success("Factura enviada por correo correctamente");
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err.response?.data?.error || "Error enviando por correo");
    } finally {
      setEmailSending(false);
    }
  };

  const inputCls =
    "w-full border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-all";
  const options = createForm.targetType === "cliente" ? clientes : propietarios;

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Cabecera */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-100 rounded-xl">
              <ReceiptText className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">
                Facturas de venta
              </h1>
              <p className="text-sm text-gray-500">
                Genera y consulta facturas por cliente o propietario
              </p>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Nueva factura
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha inicio
              </label>
              <input
                type="date"
                className={inputCls}
                value={filters.startDate}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, startDate: e.target.value }));
                  setPage(1);
                }}
              />
            </div>
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fecha fin
              </label>
              <input
                type="date"
                className={inputCls}
                value={filters.endDate}
                onChange={(e) => {
                  setFilters((p) => ({ ...p, endDate: e.target.value }));
                  setPage(1);
                }}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-brand-400" />
                <input
                  type="text"
                  className={`${inputCls} pl-8`}
                  placeholder="Factura, cliente o propietario"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-brand-50 border border-brand-100 rounded-xl px-3 py-2">
              Total: <strong className="text-brand-700">{total}</strong>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <CalendarRange className="h-3.5 w-3.5 text-brand-400" />
            <span className="text-xs text-brand-600 bg-brand-50 border border-brand-200 rounded-full px-3 py-0.5">
              {filters.startDate} → {filters.endDate}
            </span>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-2xl border border-brand-100 shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-brand-gradient text-white">
                  {[
                    "Factura",
                    "Fecha",
                    "Estado",
                    "Cliente",
                    "Propietario",
                    "Órdenes",
                    "Total exámenes",
                    "Acción",
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap ${i === 6 ? "text-right" : "text-left"}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-50">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-3 text-brand-400">
                        <div className="h-7 w-7 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                        <span className="text-sm">Cargando facturas...</span>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan="8"
                      className="py-12 text-center text-gray-400 text-sm"
                    >
                      No hay facturas para los filtros aplicados.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => {
                    const isAnulada = row.anulada === true || row.anulada === "true";
                    const tienePagos = row.tiene_pagos === true || row.tiene_pagos === "true";
                    return (
                      <tr
                        key={row.factura}
                        className={`transition-colors ${isAnulada ? "bg-red-50/40" : i % 2 === 0 ? "bg-white" : "bg-brand-50/30"} hover:bg-brand-50`}
                      >
                        <td className="px-4 py-3">
                          <span className={`font-mono text-xs px-2 py-0.5 rounded font-bold ${isAnulada ? "bg-red-100 text-red-700 line-through" : "bg-brand-100 text-brand-700"}`}>
                            {row.factura}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {row.fecha_factura}
                        </td>
                        <td className="px-4 py-3">
                          {isAnulada ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                              <Ban className="h-3 w-3" /> ANULADA
                            </span>
                          ) : tienePagos ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 bg-brand-100 border border-brand-200 rounded-full px-2 py-0.5">
                              Con abonos (Caja)
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                              Activa
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.cliente || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {row.propietario || "N/A"}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{row.ordenes}</td>
                        <td className="px-4 py-3 text-right font-semibold text-brand-700">
                          {Number(row.total_examenes || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <button
                              onClick={() => printInvoice(row.factura)}
                              className="flex items-center gap-1.5 text-brand-600 hover:text-brand-800 font-medium text-xs transition-colors"
                            >
                              <Printer className="h-3.5 w-3.5" /> PDF
                            </button>
                            {!isAnulada && !tienePagos && (
                              <button
                                onClick={() => openEditModal(row)}
                                className="flex items-center gap-1.5 text-amber-600 hover:text-amber-800 font-medium text-xs transition-colors"
                                title="Editar factura"
                              >
                                <Pencil className="h-3.5 w-3.5" /> Editar
                              </button>
                            )}
                            {!isAnulada && (
                              <button
                                onClick={() => openWAModal(row)}
                                className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-800 font-medium text-xs transition-colors"
                                title="Enviar por WhatsApp"
                              >
                                <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                              </button>
                            )}
                            {!isAnulada && (
                              <button
                                onClick={() => openEmailModal(row)}
                                className="flex items-center gap-1.5 text-sky-600 hover:text-sky-800 font-medium text-xs transition-colors"
                                title="Enviar por correo"
                              >
                                <Mail className="h-3.5 w-3.5" /> Correo
                              </button>
                            )}
                            {!isAnulada && !tienePagos && (
                              <button
                                onClick={() => openAnularModal(row)}
                                className="flex items-center gap-1.5 text-red-600 hover:text-red-800 font-medium text-xs transition-colors"
                                title="Anular factura"
                              >
                                <Ban className="h-3.5 w-3.5" /> Anular
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                className="px-3 py-1.5 border border-brand-200 rounded-lg disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Anterior
              </button>
              <button
                className="px-3 py-1.5 border border-brand-200 rounded-lg disabled:opacity-40 hover:bg-brand-50 transition-all"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal crear factura */}
      {showModal && (
        <ModalWrapper
          onClose={() => {
            setShowModal(false);
            resetCreateState();
          }}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-800">Nueva factura</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetCreateState();
                }}
                className="p-1.5 rounded-lg hover:bg-brand-50 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Facturar por
                </label>
                <select
                  className={inputCls}
                  value={createForm.targetType}
                  onChange={(e) =>
                    setCreateForm((p) => ({
                      ...p,
                      targetType: e.target.value,
                      targetCode: "",
                    }))
                  }
                >
                  <option value="cliente">Cliente</option>
                  <option value="propietario">Convenio</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {createForm.targetType === "cliente" ? "Cliente" : "Convenio"}
                </label>
                <SelectField
                  key={createForm.targetType}
                  name="targetCode"
                  value={createForm.targetCode}
                  options={options}
                  valueKey={
                    createForm.targetType === "cliente" ? "clte_codigo" : "nit"
                  }
                  displayFn={(opt) =>
                    createForm.targetType === "cliente"
                      ? `${opt.clte_codigo} - ${opt.razon || opt.nombre}`
                      : `${opt.nit} - ${opt.nom1 || ""} ${opt.ape1 || ""}`
                  }
                  placeholder="Buscar cliente o convenio..."
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, targetCode: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Fecha de la factura
                </label>
                <input
                  type="date"
                  className={inputCls}
                  value={createForm.fechaFactura}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, fechaFactura: e.target.value }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Período desde
                  </label>
                  <input
                    type="date"
                    className={inputCls}
                    value={createForm.startDate}
                    onChange={(e) =>
                      setCreateForm((p) => ({
                        ...p,
                        startDate: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Período hasta
                  </label>
                  <input
                    type="date"
                    className={inputCls}
                    value={createForm.endDate}
                    onChange={(e) =>
                      setCreateForm((p) => ({ ...p, endDate: e.target.value }))
                    }
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Costo domicilio
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className={inputCls}
                  value={createForm.domicilio}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, domicilio: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetCreateState();
                }}
                disabled={creating || previewing}
              >
                Cancelar
              </Button>
              <Button onClick={openPreview} disabled={creating || previewing}>
                {previewing ? "Preparando..." : "Ver resumen"}
              </Button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* Modal preview */}
      {showPreviewModal && (
        <ModalWrapper onClose={() => setShowPreviewModal(false)} wide>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Resumen de factura
                </h3>
                <p className="text-sm text-gray-500">
                  Cliente: <strong>{previewMeta.cliente}</strong> · Propietario:{" "}
                  <strong>{previewMeta.propietario}</strong>
                  {createForm.fechaFactura && (
                    <> · Fecha: <strong>{createForm.fechaFactura}</strong></>
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-1.5 rounded-lg hover:bg-brand-50 text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 flex justify-end">
              <label className="text-sm flex items-center gap-2 text-gray-700">
                Descuento global (%)
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="w-24 border border-brand-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-brand-400"
                  value={globalDiscountPct}
                  onChange={(e) =>
                    setGlobalDiscountPct(clampPct(e.target.value))
                  }
                />
              </label>
            </div>

            <div className="overflow-x-auto rounded-xl border border-brand-100">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-brand-gradient text-white">
                    {[
                      "Orden",
                      "Fecha",
                      "Paciente",
                      "Examen",
                      "Precio base",
                      "Desc. item %",
                      "Final",
                    ].map((h, i) => (
                      <th
                        key={h}
                        className={`px-3 py-2.5 text-left font-semibold uppercase tracking-wider ${i >= 4 ? "text-right" : ""}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-50">
                  {previewItems.map((item) => {
                    const key = lineKey(item);
                    const base = Math.max(0, toNumber(item.precio_base));
                    const itemPct = clampPct(item.descuento_item_pct);
                    const final =
                      base *
                      (1 - itemPct / 100) *
                      (1 - clampPct(globalDiscountPct) / 100);
                    return (
                      <tr key={key} className="hover:bg-brand-50">
                        <td className="px-3 py-2">{item.paciente_cod}</td>
                        <td className="px-3 py-2">{item.fecha}</td>
                        <td className="px-3 py-2">{item.paciente}</td>
                        <td className="px-3 py-2">
                          {item.examen_nombre || item.examen_cod}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="w-24 border border-brand-200 rounded px-2 py-1 text-right focus:outline-none focus:border-brand-400"
                            value={base}
                            onChange={(e) =>
                              updatePreviewItem(
                                key,
                                "precio_base",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.01"
                            className="w-20 border border-brand-200 rounded px-2 py-1 text-right focus:outline-none focus:border-brand-400"
                            value={itemPct}
                            onChange={(e) =>
                              updatePreviewItem(
                                key,
                                "descuento_item_pct",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-brand-700">
                          {itemPct === 100 ? (
                            <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                              CORTESÍA
                            </span>
                          ) : (
                            final.toFixed(2)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 ml-auto w-full max-w-xs bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm space-y-2">
              {[
                ["Subtotal:", previewComputed.subtotal],
                ["Domicilio:", previewComputed.domicilio],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between text-gray-600">
                  <span>{l}</span>
                  <strong>{v.toFixed(2)}</strong>
                </div>
              ))}
              <div className="flex justify-between font-bold text-brand-700 border-t border-brand-200 pt-2">
                <span>Total:</span>
                <span>{previewComputed.total.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPreviewModal(false)}
                disabled={creating}
              >
                Seguir editando
              </Button>
              <Button onClick={createInvoice} disabled={creating}>
                {creating ? "Generando..." : "Aceptar y generar factura"}
              </Button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* Modal editar factura */}
      {showEditModal && (
        <ModalWrapper onClose={() => setShowEditModal(false)} wide>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Editar factura
                </h3>
                <p className="text-sm text-gray-500">
                  <span className="font-mono font-bold text-brand-700">
                    {editFactura}
                  </span>{" "}
                  · Cliente: <strong>{editMeta.cliente}</strong> · Propietario:{" "}
                  <strong>{editMeta.propietario}</strong>
                </p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-1.5 rounded-lg hover:bg-brand-50 text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {editLoadingItems ? (
              <div className="flex flex-col items-center gap-3 py-12 text-brand-400">
                <div className="h-7 w-7 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
                <span className="text-sm">Cargando items...</span>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto rounded-xl border border-brand-100">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="bg-brand-gradient text-white">
                        {["Orden", "Fecha", "Paciente", "Examen", "Precio base", "Desc. item %", "Final"].map(
                          (h, i) => (
                            <th
                              key={h}
                              className={`px-3 py-2.5 font-semibold uppercase tracking-wider ${i >= 4 ? "text-right" : "text-left"}`}
                            >
                              {h}
                            </th>
                          ),
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-50">
                      {editItems.map((item) => {
                        const key = [
                          item.paciente_cod,
                          item.fecha,
                          item.sede_codigo,
                          item.examen_cod,
                          item.reg_exa,
                        ].join("|");
                        const pct = clampPct(item.descuento_pct);
                        const final = editItemFinal(item);
                        return (
                          <tr key={key} className="hover:bg-brand-50">
                            <td className="px-3 py-2">{item.paciente_cod}</td>
                            <td className="px-3 py-2">{item.fecha}</td>
                            <td className="px-3 py-2">{item.paciente}</td>
                            <td className="px-3 py-2">
                              {item.examen_nombre || item.examen_cod}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="w-24 border border-brand-200 rounded px-2 py-1 text-right focus:outline-none focus:border-brand-400"
                                value={toNumber(item.precio_original)}
                                onChange={(e) =>
                                  updateEditItem(key, "precio_original", e.target.value)
                                }
                              />
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                className="w-20 border border-brand-200 rounded px-2 py-1 text-right focus:outline-none focus:border-brand-400"
                                value={pct}
                                onChange={(e) =>
                                  updateEditItem(key, "descuento_pct", e.target.value)
                                }
                              />
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-brand-700">
                              {pct === 100 ? (
                                <span className="inline-flex items-center text-xs font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                                  CORTESÍA
                                </span>
                              ) : (
                                final.toFixed(2)
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 ml-auto w-full max-w-xs bg-brand-50 border border-brand-100 rounded-xl p-4 text-sm">
                  <div className="flex justify-between font-bold text-brand-700">
                    <span>Subtotal:</span>
                    <span>{editSubtotal.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEditModal(false)}
                    disabled={editSaving}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={saveEditInvoice} disabled={editSaving}>
                    {editSaving ? "Guardando..." : "Guardar cambios"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </ModalWrapper>
      )}

      {/* Modal confirmación anulación */}
      {showAnularModal && anularRow && (
        <ModalWrapper onClose={() => { setShowAnularModal(false); setAnularRow(null); }}>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-xl">
                <Ban className="h-5 w-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800">Anular factura</h3>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              ¿Confirma que desea anular la factura{" "}
              <strong className="font-mono text-red-700">{anularRow.factura}</strong>?
            </p>
            <p className="text-xs text-gray-500 mb-5">
              Las órdenes asociadas quedarán disponibles para ser facturadas nuevamente. Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowAnularModal(false); setAnularRow(null); }}
                disabled={anulando}
              >
                Cancelar
              </Button>
              <button
                onClick={confirmarAnular}
                disabled={anulando}
                className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {anulando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
                {anulando ? "Anulando..." : "Confirmar anulación"}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* Modal envío factura por WhatsApp */}
      {showWAModal && waRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-100 rounded-xl">
                  <MessageCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar factura por WhatsApp
                </h3>
              </div>
              <button
                onClick={() => setShowWAModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Factura:</span> {waRow.factura}
                </p>
                <p>
                  <span className="font-medium">Fecha:</span>{" "}
                  {waRow.fecha_factura}
                </p>
                {waRow.cliente && (
                  <p>
                    <span className="font-medium">Cliente:</span>{" "}
                    {waRow.cliente}
                  </p>
                )}
                {waRow.propietario && (
                  <p>
                    <span className="font-medium">Propietario:</span>{" "}
                    {waRow.propietario}
                  </p>
                )}
                <p>
                  <span className="font-medium">Total:</span> $
                  {Number(waRow.total_examenes || 0).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Número de WhatsApp del destinatario
                </label>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    value={waPhone}
                    onChange={(e) => setWaPhone(e.target.value)}
                    disabled={!waEditing}
                    placeholder="Ej: 3001234567"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setWaEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${waEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={waEditing ? "Cancelar edición" : "Editar número"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {waEditing && waPhone !== waPhoneOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El número actualizado se guardará en la base de datos al
                    enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowWAModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendWAFactura}
                disabled={waSending || !waPhone.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {waSending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {waSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal envío factura por correo */}
      {showEmailModal && emailRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-sky-100 rounded-xl">
                  <Mail className="h-5 w-5 text-sky-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-800">
                  Enviar factura por correo
                </h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-700 space-y-1">
                <p>
                  <span className="font-medium">Factura:</span> {emailRow.factura}
                </p>
                <p>
                  <span className="font-medium">Fecha:</span> {emailRow.fecha_factura}
                </p>
                {emailRow.cliente && (
                  <p>
                    <span className="font-medium">Cliente:</span> {emailRow.cliente}
                  </p>
                )}
                {emailRow.propietario && (
                  <p>
                    <span className="font-medium">Propietario:</span> {emailRow.propietario}
                  </p>
                )}
                <p>
                  <span className="font-medium">Total:</span> ${Number(emailRow.total_examenes || 0).toFixed(2)}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Correo del destinatario
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    disabled={!emailEditing}
                    placeholder="ejemplo@correo.com"
                    className="flex-1 border border-brand-200 rounded-xl px-3 py-2 text-sm bg-brand-50 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <button
                    onClick={() => setEmailEditing((v) => !v)}
                    className={`p-2 rounded-xl border transition-all ${emailEditing ? "bg-brand-100 border-brand-300 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-200"}`}
                    title={emailEditing ? "Cancelar edición" : "Editar correo"}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                </div>
                {emailEditing && emailAddress !== emailAddressOriginal && (
                  <p className="text-xs text-amber-600 mt-1">
                    El correo actualizado se guardará en la base de datos al enviar.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-2 justify-end p-5 border-t border-gray-100">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmailFactura}
                disabled={emailSending || !emailAddress.trim()}
                className="inline-flex items-center gap-2 px-5 py-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {emailSending ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default InvoicesPage;
